import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useField } from 'formik';
import isEmpty from 'lodash/isEmpty';
import { Button } from '@storable/canaveral-components';
import { Row, Col } from 'react-bootstrap';
import { resolveContentUrl } from '../../../modules/assets';
import useFileUpload from '../../hooks/useFileUpload';
import FormFieldLabel from '../FormFieldLabel';
import styles from './styles.scss';

const propTypes = {
  className: PropTypes.string,
  description: PropTypes.string,
  disabled: PropTypes.bool,
  filePath: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  onUpload: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  uploadErrors: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.number.isRequired,
      message: PropTypes.string.isRequired,
    }).isRequired,
  ),
};

const defaultProps = {
  className: undefined,
  description: undefined,
  disabled: undefined,
  filePath: undefined,
  loading: undefined,
  placeholder: undefined,
  required: undefined,
  uploadErrors: undefined,
};

const FormFieldFaviconUpload = ({
  className,
  description,
  disabled,
  name,
  filePath,
  label,
  loading,
  onUpload,
  placeholder,
  required,
  uploadErrors,
  ...rest
}) => {
  const uploadRef = useRef();
  const inputRef = useFileUpload(filePath);

  const [field, meta] = useField(name);
  const error = meta.touched && !isEmpty(meta.error);

  const src = field.value ? resolveContentUrl(field.value) : placeholder;
  const onClick = () => uploadRef.current.click();

  const fieldClasses = cx(
    styles.field,
    className,
  );

  return (
    <div className={fieldClasses}>
      <FormFieldLabel
        description={description}
        htmlFor={name}
        required={required}
        title={label}
      />
      <Row>
        <Col xs={4}>
          <div className={styles.tab}>
            <img
              alt="favicon"
              src={src}
              className={styles.favicon}
            />
            Page Name
          </div>
        </Col>
        <Col xs={8}>
          <Button
            context="neutral"
            disabled={disabled}
            icon="upload"
            onClick={onClick}
          >
            Upload
          </Button>
          <input
            {...rest}
            className={styles.input}
            disabled={disabled}
            id={name}
            ref={uploadRef}
            type="file"
            onChange={onUpload}
          />
        </Col>
      </Row>
      <input
        data-testid="form-field-favicon-upload"
        {...field}
        className={styles.input}
        disabled={disabled}
        readOnly={!disabled}
        ref={inputRef}
        required={required}
        tabIndex="-1"
        type="text"
      />
      {error && (
        <div className={styles.message}>
          {meta.error}
        </div>
      )}
      {uploadErrors && (
        <div className={styles.message}>
          {uploadErrors.map(uploadError => (
            <div key={uploadError.code}>
              {uploadError.message}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

FormFieldFaviconUpload.propTypes = propTypes;
FormFieldFaviconUpload.defaultProps = defaultProps;

export default FormFieldFaviconUpload;
